import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { fetchUsers, fetchUserCount, deleteUser } from '../actions/users.actions';
import { fetchDealershipUsers } from '../actions/dealership.actions';
import { assignRole, removeRole } from '../actions/roles.actions';
import { updateUser } from '../actions/user.actions';
import ListState from '../states/list.state';
import { fetchUserGroupUsersByEmail } from '../actions/userGroups.actions';

const initialState: ListState = {
  loading: false,
  count: 0,
  data: [],
  error: null,
  meta: null,
};

const UsersSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.data = action.payload.data;
        state.meta = action.payload.meta;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? null;
      })

      .addCase(updateUser.pending, (state) => {
        state.error = null;
      })
      .addCase(updateUser.fulfilled, (state, action: PayloadAction<any>) => {
        const updatedUser = action.payload.user || action.payload;
        state.data = [...state.data].map((user:any)=>user?.id===updatedUser?.id ? {...user,...updatedUser} : user);
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.error = action.payload ?? null;
      })

      .addCase(deleteUser.pending, (state) => {
        state.error = null;
      })
      .addCase(deleteUser.fulfilled, (state) => {
        // The list will be refetched by the action, so we don't need to update state here
        state.error = null;
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.error = action.payload ?? null;
      })

      .addCase(fetchDealershipUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDealershipUsers.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.data = action.payload.data;
        state.meta = action.payload.meta;
      })
      .addCase(fetchDealershipUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? null;
      })
      
      .addCase(assignRole.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(assignRole.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.data = [...state.data].map((user:any)=>{return [...action.payload.roleUsers].find(({id})=>user?.id===id)??user});
      })
      .addCase(assignRole.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? null;
      })

      .addCase(removeRole.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeRole.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.data = [...state.data].filter((user:any)=> ![...action.payload.roleUsers].find(({id})=>user?.id===id));
      })
      .addCase(removeRole.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? null;
      })

      .addCase(fetchUserCount.fulfilled, (state, action: PayloadAction<any>) => {
        state.count = action.payload ?? 0;
      })

      .addCase(fetchUserGroupUsersByEmail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserGroupUsersByEmail.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.data = action.payload.data;
        state.meta = action.payload.meta;
      })
      .addCase(fetchUserGroupUsersByEmail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? null;
      });

  },
});

export const { clearError } = UsersSlice.actions;
export default UsersSlice.reducer;